import React, { useState } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { auth, googleProvider } from "./firebase";
import { Wallet } from "lucide-react";
import LegalPage from "./LegalPage";

const LoginPage = () => {
  // アプリ内ブラウザ検知
  const isInAppBrowser = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return /Line|Instagram|FBAN|FBAV|Twitter|TikTok/i.test(ua);
  };
  const [inAppBrowserWarning] = useState(isInAppBrowser());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      if (
        err.code === 'auth/wrong-password' ||
        err.code === 'auth/invalid-credential'
      ) {
        try {
          const methods = await fetchSignInMethodsForEmail(auth, email);
          if (methods.includes('google.com')) {
            setShowLinkPrompt(true);
            return;
          }
        } catch (e) {}
      }
      const msg = {
        "auth/user-not-found": "メールアドレスが見つかりません",
        "auth/wrong-password": "パスワードが正しくありません",
        "auth/email-already-in-use": "このメールアドレスはすでに使用されています",
        "auth/weak-password": "パスワードは6文字以上にしてください",
        "auth/invalid-email": "メールアドレスの形式が正しくありません",
        "auth/invalid-credential": "メールアドレスまたはパスワードが正しくありません",
      }[err.code] || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (showLegal) {
    return <LegalPage onBack={() => setShowLegal(false)} />;
  }

  return (
    <div className="min-h-screen bg-page flex items-center justify-center px-4">
      <div className="card w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div
            className="p-2 rounded-xl flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, var(--color-accent), var(--color-accent-soft))",
            }}
          >
            <Wallet size={22} color="#fff" />
          </div>
          <span className="text-xl font-bold tracking-tight text-color">
            ポケ帳
          </span>
        </div>

        {inAppBrowserWarning && (
          <div
            className="p-3 rounded-xl mb-4 text-xs"
            style={{
              background: "rgba(251,191,36,0.15)",
              border: "1px solid rgba(251,191,36,0.4)",
              color: "var(--color-text)",
            }}
          >
            <div className="font-bold mb-2">⚠️ ブラウザで開いてください</div>
            <p className="mb-2 leading-relaxed">
              LINE・Instagram等のアプリ内ブラウザでは、Googleログインが動作しません。
              Chrome・Safariで開き直してください。
            </p>
            <button
              onClick={() => {
                navigator.clipboard.writeText("https://pokecho.net");
                alert("URLをコピーしました！Chrome/Safariに貼り付けてください。");
              }}
              className="btn-primary text-xs py-2 px-3"
              style={{ background: "rgba(251,191,36,0.9)", color: "#fff" }}
            >
              📋 URLをコピー
            </button>
          </div>
        )}

        <h2 className="text-lg font-bold text-color mb-6 text-center">
          {isSignUp ? "アカウント作成" : "ログイン"}
        </h2>

        <form onSubmit={handleEmailAuth} className="flex flex-col gap-3 mb-4">
          <input
            type="email"
            placeholder="メールアドレス"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-field"
            required
          />
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="パスワード（6文字以上）"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field w-full pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer opacity-60 hover:opacity-100"
              style={{ color: "var(--color-text)" }}
            >
              {showPassword ? "🙈" : "👁"}
            </button>
          </div>
          {error && <p className="text-xs" style={{ color: "var(--color-expense)" }}>{error}</p>}
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? "処理中..." : isSignUp ? "アカウント作成" : "ログイン"}
          </button>
        </form>

        <button
          onClick={handleGoogle}
          disabled={loading}
          className="btn-primary w-full mb-4 flex items-center justify-center gap-2"
          style={{
            background: "#fff",
            color: "#333",
            border: "1px solid #ddd",
            fontWeight: 600,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Googleでログイン
        </button>

        <button
          onClick={() => {
            setIsSignUp(!isSignUp);
            setError("");
          }}
          className="text-sm w-full text-center bg-transparent border-none cursor-pointer hover:opacity-80"
          style={{ color: "var(--color-accent)" }}
        >
          {isSignUp
            ? "すでにアカウントをお持ちの方はこちら"
            : "アカウントを作成する"}
        </button>

        <button
          onClick={() => setShowLegal(true)}
          className="text-xs w-full text-center bg-transparent border-none cursor-pointer hover:opacity-80 mt-3"
          style={{ color: "var(--color-text-muted)" }}
        >
          利用規約・プライバシーポリシー
        </button>
      </div>

      {showLinkPrompt && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 px-4"
          style={{ background: "rgba(0,0,0,0.6)" }}
        >
          <div className="card w-full max-w-sm p-6 flex flex-col gap-4">
            <h3 className="text-base font-bold text-color text-center">
              Googleアカウントで登録済みです
            </h3>
            <p className="text-sm text-center" style={{ color: "var(--color-text-muted)" }}>
              このメールアドレスはGoogleアカウントで登録されています。<br />
              Googleでログインしてください。
            </p>
            <button
              onClick={() => { setShowLinkPrompt(false); handleGoogle(); }}
              className="btn-primary"
            >
              Googleでログイン
            </button>
            <button
              onClick={() => setShowLinkPrompt(false)}
              className="text-sm bg-transparent border-none cursor-pointer hover:opacity-80"
              style={{ color: "var(--color-text-muted)" }}
            >
              キャンセル
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;